import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid2,
	IconButton,
	Link,
	Stack,
	Switch,
	Tooltip,
	Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useEnvironmentNavigation } from "../../hooks/useEnvironmentNavigation";
import FreelancerDetails from "../../components/common/FreelancerDetails";
import {
	useFreelancerProfile,
	useUpdateFreelancerProfileControlledStatus
} from "../../hooks/useFreelancerProfile";
import { useCurrentUser } from "../../hooks/useUser";
import { zoullooColors } from "../../theme";
import { formatDate } from "../../utils/formatting";
import { useSnackbarContext } from "../../contexts/useSnackBarContext";
import { TFunction } from "i18next";
import ErrorDisplay from "../../components/internal-user/applications/ErrorDisplay";
import ApplicationDetailsSkeleton from "./components/ApplicationDetailsSkeleton";
import { UserRole } from "../../models/UserRole";
import { getProjectsByFreelancerProfile } from "../../api/project-freelancer-profiles-api";
import { ProjectFreelancerProfile } from "../../models/ProjectFreelancerProfile";
import {
	useFreelancerOnboardingStatusTracks,
	useFreelancerSoftSkillsEvaluations
} from "../../hooks/useFreelancerOnboarding";
import { FreelancerOnboardingStatus } from "../../models/enums/FreelancerOnboardingStatus";
import { FreelancerSoftSkillsEvaluationStatus } from "../../models/enums/FreelancerSoftSkillsEvaluationStatus";
import { LanguageCode, toLanguageCode } from "../../models/enums/LanguageCode";
import PresentationDraftEntry from "../../components/internal-user/presentation-drafts/PresentationDraftEntry";

interface ApplicationInfoRowProps {
	label: string;
	value?: string | React.ReactNode;
	t: TFunction;
	isLast?: boolean;
}

const ApplicationInfoRow: React.FC<ApplicationInfoRowProps> = ({
	label,
	value,
	t,
	isLast = false
}) => (
	<Box
		sx={{
			display: "flex",
			justifyContent: "space-between",
			p: 2,
			...(isLast ? {} : { borderBottom: `1px solid ${zoullooColors.grey[300]}` })
		}}
	>
		<Typography variant="body2" color="text.secondary" fontWeight={600}>
			{label}
		</Typography>
		<Typography
			variant={value === "-" ? "h4" : "body2"}
			color="text.secondary"
			textAlign={"center"}
			px={value === "-" ? 4 : 0}
		>
			{value ?? t("common.notAvailable")}
		</Typography>
	</Box>
);

const ApplicationDetails: React.FC = () => {
	const { data: currentUser } = useCurrentUser();
	const params = useParams();
	const freelancerProfileKey = params.key || "";
	const {
		data: freelancerProfile,
		isLoading,
		isError,
		refetch
	} = useFreelancerProfile(0, true, freelancerProfileKey);
	const { data: freelancerOnboardingStatusTracks } = useFreelancerOnboardingStatusTracks(
		freelancerProfile?.onboarding?.id || 0
	);
	const { data: freelancerSoftSkillsEvaluations } = useFreelancerSoftSkillsEvaluations(
		freelancerProfile?.onboarding?.id || 0
	);
	const { t } = useTranslation();
	const { navigateByEnvironment, shouldOpenInNewTab } = useEnvironmentNavigation();
	const { showSuccess } = useSnackbarContext();

	// "Contrôlé" toggle (TAM) — same confirm-then-mutate flow as the Applications
	// list, but inline in the Candidature panel. null = no pending change.
	const updateControlledMutation = useUpdateFreelancerProfileControlledStatus();
	const [controlledConfirm, setControlledConfirm] = useState<boolean | null>(null);

	const freelancerFullName = `${freelancerProfile?.freelancer?.user?.firstName ?? ""} ${
		freelancerProfile?.freelancer?.user?.lastName ?? ""
	}`.trim();

	const handleControlledConfirmed = async () => {
		if (controlledConfirm === null || !freelancerProfile?.id) {
			return;
		}
		try {
			await updateControlledMutation.mutateAsync({
				freelancerProfileId: freelancerProfile.id,
				newIsControlled: controlledConfirm
			});
			await refetch();
		} finally {
			setControlledConfirm(null);
		}
	};

	const handleCopyUrl = (url: string) => {
		navigator.clipboard.writeText(url);
		showSuccess(t("applicationDetails.urlCopied"));
	};

	// State for freelancer's associated projects (for CSM view)
	const [freelancerProjects, setFreelancerProjects] = useState<
		ProjectFreelancerProfile[]
	>([]);
	const [projectsLoading, setProjectsLoading] = useState(false);

	// Check user roles
	const isTAM = currentUser?.roles?.includes(UserRole.TAM) ?? false;
	const isCSM = currentUser?.roles?.includes(UserRole.CSM) ?? false;
	const isAdmin = currentUser?.roles?.includes(UserRole.Admin) ?? false;
	// The "Contrôlé" flag is set by any internal manager after acceptance, so the
	// Candidature panel that hosts the toggle is shown to TAM, CSM and Admin alike
	// (matches the AdminOrManagerRoles authorization on the backend endpoint).
	const isInternalUser = isTAM || isCSM || isAdmin;

	// Fetch projects associated with this freelancer (for CSM)
	useEffect(() => {
		const fetchProjects = async () => {
			if (!freelancerProfile?.id || !isCSM) return;

			setProjectsLoading(true);
			try {
				const projects = await getProjectsByFreelancerProfile(freelancerProfile.id);
				setFreelancerProjects(projects);
			} catch (error) {
				console.error("Error fetching freelancer projects:", error);
			} finally {
				setProjectsLoading(false);
			}
		};

		fetchProjects();
	}, [freelancerProfile?.id, isCSM]);

	const getLatestFreelancerApplicationSubmissionDate = () => {
		const submissionTracks = freelancerOnboardingStatusTracks?.filter(
			x =>
				x.onboardingStatus.toString() ===
				FreelancerOnboardingStatus[FreelancerOnboardingStatus.Submitted]
		);

		submissionTracks?.sort((a, b) => new Date(a.statusDate).getTime() - new Date(b.statusDate).getTime());

		const latestSubmission = submissionTracks?.[submissionTracks.length - 1];
		return latestSubmission ? formatDate(latestSubmission.statusDate.toString()) : undefined;
	};

	const getFreelancerSoftSkillsEvaluationInformation = () => {
		const freelancerSoftSkillsEvaluation = freelancerSoftSkillsEvaluations?.[0];

		switch (freelancerSoftSkillsEvaluation?.status.toString()) {
			case FreelancerSoftSkillsEvaluationStatus[
				FreelancerSoftSkillsEvaluationStatus.CreatedInternally
			]:
				return (
					<>
						<Typography>
							{t(
								`applicationDetails.${
									FreelancerSoftSkillsEvaluationStatus[
										FreelancerSoftSkillsEvaluationStatus.CreatedInternally
									]
								}`
							)}
						</Typography>
						<Typography>
							{formatDate(freelancerSoftSkillsEvaluation.createdInternallyAt?.toString())}
						</Typography>
					</>
				);

			case FreelancerSoftSkillsEvaluationStatus[
				FreelancerSoftSkillsEvaluationStatus.CreatedExternally
			]:
				return (
					<>
						<Typography>
							{t(
								`applicationDetails.${
									FreelancerSoftSkillsEvaluationStatus[
										FreelancerSoftSkillsEvaluationStatus.CreatedExternally
									]
								}`
							)}
						</Typography>
						<Typography>
							{formatDate(freelancerSoftSkillsEvaluation.createdExternallyAt?.toString())}
						</Typography>
					</>
				);
			case FreelancerSoftSkillsEvaluationStatus[
				FreelancerSoftSkillsEvaluationStatus.FreelancerInvited
			]:
				return (
					<Stack spacing={0.5} alignItems="flex-end">
						<Typography>
							{t(
								`applicationDetails.${
									FreelancerSoftSkillsEvaluationStatus[
										FreelancerSoftSkillsEvaluationStatus.FreelancerInvited
									]
								}`
							)}{" "}
							{formatDate(freelancerSoftSkillsEvaluation.invitedAt?.toString())}
						</Typography>
						{freelancerSoftSkillsEvaluation.evaluationUrl && (
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								<Link
									href={freelancerSoftSkillsEvaluation.evaluationUrl}
									target="_blank"
									rel="noopener noreferrer"
									sx={{
										color: zoullooColors.secondary.main,
										textDecoration: "none",
										"&:hover": { textDecoration: "underline" }
									}}
								>
									{t("applicationDetails.evaluationUrl")}
								</Link>
								<Tooltip title={t("applicationDetails.copyUrl")}>
									<IconButton
										size="small"
										onClick={() =>
											handleCopyUrl(freelancerSoftSkillsEvaluation.evaluationUrl)
										}
										sx={{ p: 0.5 }}
									>
										<ContentCopyIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Tooltip>
							</Box>
						)}
					</Stack>
				);
			case FreelancerSoftSkillsEvaluationStatus[
				FreelancerSoftSkillsEvaluationStatus.EvaluationCompleted
			]: {
				const reportPath =
					freelancerSoftSkillsEvaluation.reports.find(
						x => x.reportLanguage === currentUser?.settings?.preferredLanguage
					)?.reportPath ||
					freelancerSoftSkillsEvaluation.reports.find(
						x => x.reportLanguage === LanguageCode.Fr
					)?.reportPath;

				return (
					<>
						{reportPath ? (
							<Link
								variant="body1"
								color="secondary"
								sx={{
									flex: 1,
									textDecoration: "none",
									fontWeight: "medium",
									cursor: "pointer",
									border: "none",
									background: "none",
									padding: 0,
									"&:hover": {
										textDecoration: "underline"
									}
								}}
								href={`${process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL}/${process.env.REACT_APP_FREELANCER_SOFT_SKILLS_EVALUATION_COMPLETE_REPORT}/${reportPath}`}
								target="_blank"
							>
								{t(
									`applicationDetails.${
										FreelancerSoftSkillsEvaluationStatus[
											FreelancerSoftSkillsEvaluationStatus.EvaluationCompleted
										]
									}`
								)}
							</Link>
						) : (
							<Typography>
								{t(
									`applicationDetails.${
										FreelancerSoftSkillsEvaluationStatus[
											FreelancerSoftSkillsEvaluationStatus.EvaluationCompleted
										]
									}`
								)}
							</Typography>
						)}

						<Typography>
							{formatDate(freelancerSoftSkillsEvaluation.completedAt?.toString())}
						</Typography>
					</>
				);
			}
			default:
				return null;
		}
	};

	// Application info items configuration
	const getApplicationInfoItems = () => {
		const freelancerSoftSkillsEvaluationInformation =
			getFreelancerSoftSkillsEvaluationInformation();

		return [
			{
				labelKey: "applicationDetails.tam",
				value: `${currentUser?.firstName} ${currentUser?.lastName}`
			},
			{
				labelKey: "applicationDetails.registration",
				value: freelancerProfile?.freelancer?.user?.createdAt
					? formatDate(freelancerProfile?.freelancer?.user?.createdAt)
					: undefined
			},
			{
				labelKey: "applicationDetails.profileQualification",
				value: getLatestFreelancerApplicationSubmissionDate() ?? "-"
			},
			{
				labelKey: "applicationDetails.interview",
				value: "-"
			},
			{
				labelKey: "applicationDetails.softSkillsEvaluation",
				value: freelancerSoftSkillsEvaluationInformation ?? "-"
			},
			{
				labelKey: "applicationDetails.hardSkillsEvaluation",
				value: "-"
			},
			{
				labelKey: "applicationDetails.positioning",
				value: "-"
			},
			{
				labelKey: "pages.applications.card.controlled",
				value: (
					<Switch
						checked={freelancerProfile?.isControlled ?? false}
						onChange={e => setControlledConfirm(e.target.checked)}
						color="secondary"
						size="small"
						disabled={updateControlledMutation.isPending}
					/>
				)
			}
		];
	};

	if (isLoading) {
		return <ApplicationDetailsSkeleton />;
	}

	return (
		<Box px={{ xs: 2, sm: 4, md: 4 }} py={3}>
			{/* Main Content Grid */}
			<Grid2 container spacing={3}>
				{isError ? (
					<Box
						p={2}
						border={`1px solid ${zoullooColors.grey[300]}`}
						borderRadius="20px"
						width="100%"
						display="flex"
						justifyContent="center"
						alignItems="center"
					>
						<ErrorDisplay onRetry={() => refetch()} />
					</Box>
				) : (
					<>
						{/* Left Side - Freelancer Details */}
						<Grid2
							size={{ xs: 12, lg: 9 }}
							sx={{
								border: `1px solid ${zoullooColors.grey[400]}`,
								borderRadius: "20px"
							}}
						>
							{/* Freelancer Details */}
							<FreelancerDetails
								freelancerProfileId={0}
								freelancerProfileKey={freelancerProfileKey}
								isExpanded={true}
								hideLastExchange={true}
								showSocialNetworks={true}
								showDocuments={true}
								showAvailability={true}
								showAdditionalInfo={true}
								showContact={true}
								showNotes={true}
								showHeader={true}
								showEvaluations={true}
								presentationActions={
									(isTAM || isAdmin) && freelancerProfile?.id ? (
										<PresentationDraftEntry
											freelancerProfileId={freelancerProfile.id}
											freelancerProfileKey={freelancerProfileKey}
											talentFirstName={freelancerProfile.freelancer?.user?.firstName ?? ""}
											talentLanguage={toLanguageCode(
												freelancerProfile.freelancer?.user?.settings?.preferredLanguage
											)}
										/>
									) : undefined
								}
							/>
						</Grid2>

						{/* Right Side - Accordions */}
						<Grid2 size={{ xs: 12, lg: 3 }}>
							<Box
								sx={{
									position: "sticky",
									top: 24,
									alignSelf: "flex-start"
								}}
							>
								<Stack spacing={2}>
									{/* Internal staff - Onboarding/Application block + Contrôlé toggle */}
									{isInternalUser && (
										<Accordion
											defaultExpanded
											disableGutters
											sx={{
												boxShadow: "none",
												border: `1px solid ${zoullooColors.grey[300]}`,
												borderRadius: "20px !important",
												overflow: "hidden",
												"&:before": { display: "none" }
											}}
										>
											<AccordionSummary
												expandIcon={<ExpandMoreIcon />}
												aria-controls="candidature-content"
												id="candidature-header"
												sx={{
													backgroundColor: zoullooColors.grey[100],
													"& .MuiAccordionSummary-content": {
														my: 2
													}
												}}
											>
												<Typography variant="h3">
													{t("applicationDetails.application")}
												</Typography>
											</AccordionSummary>
											<AccordionDetails
												sx={{
													p: 0,
													backgroundColor: zoullooColors.background.default
												}}
											>
												<Stack spacing={0}>
													{getApplicationInfoItems().map((item, index, array) => (
														<ApplicationInfoRow
															key={item.labelKey}
															label={t(item.labelKey)}
															value={item.value}
															t={t}
															isLast={index === array.length - 1}
														/>
													))}
												</Stack>
											</AccordionDetails>
										</Accordion>
									)}

									{/* CSM Only - Associated Projects Block */}
									{isCSM && (
										<Accordion
											defaultExpanded
											disableGutters
											sx={{
												boxShadow: "none",
												border: `1px solid ${zoullooColors.grey[300]}`,
												borderRadius: "20px !important",
												overflow: "hidden",
												"&:before": { display: "none" }
											}}
										>
											<AccordionSummary
												expandIcon={<ExpandMoreIcon />}
												aria-controls="projects-content"
												id="projects-header"
												sx={{
													backgroundColor: zoullooColors.grey[100],
													"& .MuiAccordionSummary-content": {
														my: 2
													}
												}}
											>
												<Typography variant="h3">
													{t("applicationDetails.associatedProjects")}
												</Typography>
											</AccordionSummary>
											<AccordionDetails
												sx={{
													p: 0,
													backgroundColor: zoullooColors.background.default,
													maxHeight: 300,
													overflowY: "auto"
												}}
											>
												{projectsLoading ? (
													<Box p={2} textAlign="center">
														<Typography variant="body2" color="text.secondary">
															{t("common.loading")}
														</Typography>
													</Box>
												) : freelancerProjects.length === 0 ? (
													<Box p={2} textAlign="center">
														<Typography variant="body2" color="text.secondary">
															{t("applicationDetails.noAssociatedProjects")}
														</Typography>
													</Box>
												) : (
													<Stack spacing={0}>
														{freelancerProjects.map((pfp, index) => (
															<Box
																key={pfp.id}
																sx={{
																	display: "flex",
																	justifyContent: "space-between",
																	alignItems: "center",
																	p: 2,
																	...(index < freelancerProjects.length - 1
																		? {
																				borderBottom: `1px solid ${zoullooColors.grey[300]}`
																		  }
																		: {})
																}}
															>
																<Link
																	component="button"
																	onClick={() => navigateByEnvironment(`/projects/${pfp.projectId}`)}
																	sx={{
																		display: "flex",
																		alignItems: "center",
																		gap: 0.5,
																		color: zoullooColors.secondary.main,
																		textDecoration: "none",
																		cursor: "pointer",
																		border: "none",
																		background: "none",
																		"&:hover": {
																			textDecoration: "underline"
																		}
																	}}
																>
																	<Typography variant="body2" fontWeight={500}>
																		{pfp.projectName || `Project #${pfp.projectId}`}
																	</Typography>
																	{shouldOpenInNewTab && <OpenInNewIcon sx={{ fontSize: 14 }} />}
																</Link>
															</Box>
														))}
													</Stack>
												)}
											</AccordionDetails>
										</Accordion>
									)}
								</Stack>
							</Box>
						</Grid2>
					</>
				)}
			</Grid2>

			{/* "Contrôlé" change confirmation — mirrors the Applications list flow */}
			<Dialog
				open={controlledConfirm !== null}
				onClose={() => setControlledConfirm(null)}
				aria-labelledby="controlled-change-dialog-title"
				aria-describedby="controlled-change-dialog-description"
			>
				<DialogTitle id="controlled-change-dialog-title">
					{t("pages.applications.confirmControlledChange.title")}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="controlled-change-dialog-description">
						<Trans
							i18nKey={"pages.applications.confirmControlledChange.message"}
							components={{ strong: <strong /> }}
							values={{ freelancerName: freelancerFullName }}
						/>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						disableRipple
						color="secondary"
						onClick={() => setControlledConfirm(null)}
						disabled={updateControlledMutation.isPending}
					>
						{t("common.cancel")}
					</Button>
					<Button
						disableRipple
						color="secondary"
						variant="contained"
						onClick={handleControlledConfirmed}
						disabled={updateControlledMutation.isPending}
					>
						{t("common.confirm")}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ApplicationDetails;
